import {computed, inject, Injectable, signal} from '@angular/core';
import {UsersStore} from '../users/users.store';
import {TicketsStore} from '../tickets/stores/tickets.store';
import {Ticket} from '../tickets/models/ticket.model';
import {UserProfileStore} from '../../core/auth/stores/user-profile.store';

@Injectable({ providedIn: 'root' })
export class DashboardContext {
	private usersStore = inject(UsersStore)
	private profileStore = inject(UserProfileStore)
	private ticketsStore = inject(TicketsStore)
	private readonly _initialLoadComplete = signal(false)

	readonly initialLoadComplete = this._initialLoadComplete.asReadonly()

	readonly isLoading = computed( () => {
		return this.usersStore.loading()
			|| this.ticketsStore.loading()
			|| this.ticketsStore.dashboardTicketsLoading()
			|| this.ticketsStore.dashboardCategorizationLoading()
			|| this.ticketsStore.dashboardStatsLoading()
	})

	readonly donutLoading = this.ticketsStore.dashboardStatsLoading

	readonly usersTicketsLoading = computed(() => {
		return this.usersStore.loading()
			|| this.ticketsStore.dashboardTicketsLoading()
	})

	readonly categorizationLoading = this.ticketsStore.dashboardCategorizationLoading

	markInitialLoadPending() {
		this._initialLoadComplete.set(false)
	}

	markInitialLoadComplete() {
		this._initialLoadComplete.set(true)
	}

	readonly usersListTickets = computed(() => {
		const users = this.usersStore.users()
		const tickets = this.visibleDashboardTickets()

		return users.map(u => {
			const excludedStatuses = ['MERGED', 'CLOSED'];

			const usersTickets = tickets.filter(t =>
				t.assignee_user_id === u.id &&
				t.category === 'SUPPORT' &&
				!excludedStatuses.includes(t.status)
			);

			const latestInProgressTicket = usersTickets
				.filter(t => t.status === 'IN_PROGRESS')
				.sort((a, b) =>
					new Date(b.last_inbound_at).getTime() - new Date(a.last_inbound_at).getTime()
				)[0] ?? null;

			const highestPriorityTicket = usersTickets.reduce((best, t) => {
				if (!best) return t;

				if (t.priority > best.priority) return t;
				if (t.priority < best.priority) return best;

				return new Date(t.last_inbound_at) < new Date(best.last_inbound_at) ? t : best;
			}, null as Ticket | null);


			return {
				user: `${u.name} ${u.surname}`,
				newest_in_progress_ticket: latestInProgressTicket ?? null,
				number_assigned_ticket: usersTickets.length,
				highest_proprity_ticket: highestPriorityTicket ?? null
			}
		}).slice(0, 5)
	})

	readonly visibleTickets = computed(() => {
		const effective = this.profileStore.effectiveRole();
		const all = this.ticketsStore.tickets();

		if (!effective) return [];

		if (effective === 'TENANT_ADMIN' || effective === 'MAILBOX_ADMIN') return all;

		const userId = this.profileStore.user()?.id;
		return all.filter(t => {
			if (t.category === 'SUPPORT') {
				return !t.assignee_user_id || t.assignee_user_id === userId;
			}
			return true;
		});
	});

	readonly visibleDashboardTickets = computed(() => {
		const effective = this.profileStore.effectiveRole();
		const all = this.ticketsStore.dashboardTickets();

		if (!effective) return [];

		if (effective === 'TENANT_ADMIN' || effective === 'MAILBOX_ADMIN') return all;

		const userId = this.profileStore.user()?.id;
		return all.filter(t => {
			if (t.category === 'SUPPORT') {
				return !t.assignee_user_id || t.assignee_user_id === userId;
			}
			return true;
		});
	});

	readonly visibleDashboardStats = computed(() => {
		const effective = this.profileStore.effectiveRole();
		const all = this.ticketsStore.dashboardStats();

		if (!effective) return [];

		if (effective === 'TENANT_ADMIN' || effective === 'MAILBOX_ADMIN') return all;

		const userId = this.profileStore.user()?.id;
		return all.filter(t => {
			if (t.category === 'SUPPORT') {
				return !t.assignee_user_id || t.assignee_user_id === userId;
			}
			return true;
		});
	});

	readonly myTickets = computed(() => {
		const userId = this.profileStore.user()?.id;
		if (!userId) return [];

		return this.visibleDashboardTickets().filter(t =>
			t.assignee_user_id === userId &&
			t.category === 'SUPPORT' &&
			!['MERGED', 'CLOSED'].includes(t.status)
		);
	});

	readonly assigneeRequests = computed(() => {
		const usersMap = new Map(
			this.usersStore.users().map(u => [u.id, u])
		)

		const ticketsMap = new Map(
			[
				...this.ticketsStore.tickets(),
				...this.ticketsStore.dashboardTickets()
			].map(t => [t.id, t])
		)

		return this.ticketsStore.assigneeRequests()
			.filter(r => r.status === 'PENDING')
			.flatMap(r => {
			const oldUser = usersMap.get(r.old_assignee_user_id)
			const newUser = usersMap.get(r.requested_assignee_user_id)
			const ticket = ticketsMap.get(r.ticket_id)

			if (!oldUser || !newUser || !ticket) return []

			return [{
				...r,
				old_user_label: `${oldUser.name} ${oldUser.surname}`,
				new_user_label: `${newUser.name} ${newUser.surname}`,
				ticket_subject: ticket.subject,
			}]
		})
	})

	readonly pendingTicketsListAssigneeChange = computed( () => {
		return this.assigneeRequests().map( r => r.ticket_id )
	})

	readonly ticketToCategorize = computed(() => {
		return this.visibleDashboardTickets().filter(t => t.category === 'NOT_CATEGORIZED')
	})

	readonly uncategorizedTicketsNumber = computed(() => {
		return this.visibleDashboardStats().filter(t => t.category === 'NOT_CATEGORIZED').length
	})

	readonly allSupportTickets = computed(() => {
		return this.visibleDashboardStats().filter(t => t.category === 'SUPPORT')
	})

	readonly allTicketsNumber = computed(() => {
		return this.allSupportTickets().length
	})
	readonly closedTicketsNumber = computed(() => {
		return this.allSupportTickets().filter(t => t.status === 'CLOSED').length
	})
	readonly noAssignedTicketsNumber = computed(() => {
		return this.allSupportTickets().filter(t => !t.assignee_user_id).length
	})
	readonly wipTicketsNumber = computed(() => {
		return this.allSupportTickets().filter(t => t.status === 'IN_PROGRESS' || t.status === 'PENDING').length
	})
	readonly pendingTicketsNumber = computed(() => {
		return this.allSupportTickets().filter(t => t.status === 'PENDING').length
	})
}
